import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { UserCheck, Clock, AlertTriangle, CheckCircle, XCircle, Calendar, Plus, Eye, RefreshCw, History, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export default function ProbationManagement() {
  const [records, setRecords] = useState([]);
  const [dueRecords, setDueRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionRemarks, setActionRemarks] = useState('');
  const [extensionMonths, setExtensionMonths] = useState(3);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    employee_id: '',
    duration_months: 3
  });
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const canManage = ['admin', 'hr_manager'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const [recordsRes, dueRes, empRes] = await Promise.all([
        axios.get(`${API}/probation/records${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`),
        axios.get(`${API}/probation/due?days=30`),
        axios.get(`${API}/employees`)
      ]);
      setRecords(recordsRes.data);
      setDueRecords(dueRes.data.records || []);
      setEmployees(empRes.data);
    } catch (error) {
      toast.error('Failed to fetch probation data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    
    try {
      await axios.post(`${API}/probation/create?employee_id=${formData.employee_id}&duration_months=${formData.duration_months}`);
      toast.success('Probation record created');
      setDialogOpen(false);
      setFormData({ employee_id: '', duration_months: 3 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create probation record');
    }
  };

  const handleAction = async () => {
    if (!selectedRecord || !actionType) return;
    
    if ((actionType === 'terminate' || actionType === 'extend') && !actionRemarks.trim()) {
      toast.error('Please provide remarks');
      return;
    }
    
    try {
      const payload = {
        action: actionType,
        remarks: actionRemarks.trim() || null
      };
      
      if (actionType === 'extend') {
        payload.extension_months = extensionMonths;
      }
      
      const res = await axios.put(`${API}/probation/${selectedRecord.probation_id}/action`, payload);
      const emailInfo = (res.data && res.data.email) || {};
      if (emailInfo.status === 'sent') {
        toast.success(`Probation ${actionType}d — email sent to employee`);
      } else if (emailInfo.status === 'disabled') {
        toast.success(`Probation ${actionType}d (email service not configured)`);
      } else if (emailInfo.status === 'skipped') {
        toast.success(`Probation ${actionType}d (no employee email on file)`);
      } else if (emailInfo.status === 'error') {
        toast.warning(`Probation ${actionType}d, but email delivery failed: ${emailInfo.reason || 'unknown'}`);
      } else {
        toast.success(`Probation ${actionType}d successfully`);
      }
      setActionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType} probation`);
    }
  };

  // Export all currently-displayed probation records to Excel.
  const exportProbationToExcel = async () => {
    if (!canManage) {
      toast.error('Not authorized to export probation reports');
      return;
    }
    if (!records || records.length === 0) {
      toast.error('No probation records to export');
      return;
    }
    try {
      const XLSX = await import('xlsx');

      // Aggregated counts (mirror the 4 summary cards)
      const inProb = records.filter((r) => r.status === 'in_probation').length;
      const confirmed = records.filter((r) => r.status === 'confirmed').length;
      // Iter74 — see comment on card. Extended = any record ever extended
      // (extended_count > 0) OR currently in extended status.
      const extended = records.filter((r) => (r.extended_count || 0) > 0 || r.status === 'extended').length;
      const terminated = records.filter((r) => r.status === 'terminated').length;
      const dueSoon = dueRecords ? dueRecords.length : 0;

      const summaryAoa = [
        ['Probation Management Report'],
        ['Generated', new Date().toLocaleString('en-MY')],
        ['Filter', filterStatus === 'all' ? 'All Statuses' : filterStatus],
        [],
        ['In Probation', inProb],
        ['Due Soon (30 days)', dueSoon],
        ['Confirmed', confirmed],
        ['Extended', extended],
        ['Terminated', terminated],
        ['Total Records', records.length],
      ];
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
      summaryWs['!cols'] = [{ wch: 24 }, { wch: 22 }];

      const rows = records.map((r) => {
        const left = getDaysRemaining(r.end_date, r.extended_to);
        const isActive = r.status === 'in_probation' || r.status === 'extended';
        return {
          'Employee Name': r.employee_name || '',
          'Employee ID': r.employee_id || '',
          'Start Date': r.start_date || '',
          'Original End Date': r.end_date || '',
          'Current End Date': r.extended_to || r.end_date || '',
          'Duration (months)': r.duration_months || '',
          'Status': r.status || '',
          'Extended Count': r.extended_count || 0,
          'Days Left': isActive ? (left < 0 ? `${Math.abs(left)} overdue` : `${left}`) : '',
          'Action Date': r.action_date ? r.action_date.slice(0, 10) : '',
          'Action By': r.action_by || '',
          'Action Remarks': r.action_remarks || r.extension_reason || '',
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 16 },
        { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 14 },
        { wch: 12 }, { wch: 28 }, { wch: 32 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      XLSX.utils.book_append_sheet(wb, ws, 'Probation Records');

      const fname = `probation-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Exported ${fname}`);
    } catch (err) {
      toast.error('Excel export failed');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'in_probation': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: Clock },
      'confirmed': { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle },
      'extended': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: RefreshCw },
      'terminated': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle }
    };
    return styles[status] || styles['in_probation'];
  };

  const getDaysRemaining = (endDate, extendedTo) => {
    const end = new Date(extendedTo || endDate);
    const today = new Date();
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Get employees without probation record
  const availableEmployees = employees.filter(emp => 
    emp.status !== 'terminated' && 
    !records.some(r => r.employee_id === emp.employee_id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Probation Management</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Track and manage employee probation periods
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={exportProbationToExcel}
              disabled={!records || records.length === 0}
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 disabled:opacity-50"
              data-testid="export-probation-excel-btn"
              title={(!records || records.length === 0) ? 'No probation records to export' : 'Export current view to Excel'}
            >
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
            <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              New Probation
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">In Probation</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="probation-card-active">
            {/* Iter132 — Both `in_probation` (first time) and `extended`
                (probation renewed) are actively SERVING probation, so
                they belong in the same headline metric. Reported by
                Shikha 19-Jun-2026: card showed 0 even though Siti was
                in extended probation. */}
            {records.filter(r => r.status === 'in_probation' || r.status === 'extended').length}
          </p>
          <p className="text-xs text-slate-500 mt-1">Includes extended</p>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Due Soon</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{dueRecords.length}</p>
          <p className="text-xs text-slate-500 mt-1">Within 30 days</p>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Confirmed</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">
            {records.filter(r => r.status === 'confirmed').length}
          </p>
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Extended</span>
          </div>
          <p className="text-2xl font-bold text-blue-600" data-testid="probation-card-extended">
            {/* Iter74 (Ankur 07-Jul-2026) — was previously only counting rows
                where `status === 'extended'` (i.e. CURRENTLY in extended
                probation). But the table shows "Extended 1×" on confirmed /
                terminated rows too when they were extended at some point,
                which made the card read 0 while the table showed extensions.
                Count anyone whose probation was ever extended for symmetry
                with what testers see. */}
            {records.filter(r => (r.extended_count || 0) > 0 || r.status === 'extended').length}
          </p>
          <p className="text-xs text-slate-500 mt-1">Any record ever extended</p>
        </div>
      </div>

      {/* Due Soon Alert */}
      {dueRecords.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 dark:text-red-300">Probation Ending Soon</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                {dueRecords.length} employee(s) have probation ending within 30 days. Please take action.
              </p>
              <div className="mt-3 space-y-2">
                {dueRecords.slice(0, 5).map(record => (
                  <div key={record.probation_id} className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{record.employee_name}</p>
                      <p className="text-xs text-slate-500">
                        Ends: {new Date(record.extended_to || record.end_date).toLocaleDateString('en-MY')}
                        {record.is_overdue && <span className="text-red-500 ml-2">OVERDUE</span>}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${record.days_remaining < 0 ? 'text-red-600' : record.days_remaining <= 7 ? 'text-orange-600' : 'text-amber-600'}`}>
                        {record.days_remaining < 0 ? `${Math.abs(record.days_remaining)} days overdue` : `${record.days_remaining} days left`}
                      </span>
                      {canManage && (
                        <Button 
                          size="sm" 
                          onClick={() => { setSelectedRecord(record); setActionType('confirm'); setActionDialogOpen(true); }}
                          className="bg-emerald-600 hover:bg-emerald-700 h-8"
                        >
                          Take Action
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center space-x-4">
        <Label className="text-slate-700 dark:text-slate-300">Filter by Status:</Label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-10 rounded-lg border border-slate-300 dark:border-slate-600 px-4 dark:bg-slate-900 dark:text-slate-100"
        >
          <option value="all">All Status</option>
          <option value="in_probation">In Probation</option>
          <option value="confirmed">Confirmed</option>
          <option value="extended">Extended</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      {/* Records Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Probation Records</h3>
        </div>
        {records.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No probation records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Duration</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Days Left</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {records.map(record => {
                  const statusStyle = getStatusBadge(record.status);
                  const StatusIcon = statusStyle.icon;
                  const daysLeft = getDaysRemaining(record.end_date, record.extended_to);
                  
                  return (
                    <tr key={record.probation_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                      <td className="px-6 py-4">
                        <Link
                          to={`/dashboard/employees/${record.employee_id}`}
                          className="group inline-flex items-center gap-1"
                          data-testid={`probation-employee-link-${record.employee_id}`}
                          title="Open employee profile"
                        >
                          <span className="font-medium text-slate-900 dark:text-slate-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 group-hover:underline">
                            {record.employee_name}
                          </span>
                          <ExternalLink className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                        <p className="text-xs text-slate-500 font-mono">{record.employee_id}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {new Date(record.start_date).toLocaleDateString('en-MY')}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {new Date(record.extended_to || record.end_date).toLocaleDateString('en-MY')}
                        {record.extended_count > 0 && (
                          <span className="ml-2 text-xs text-blue-600">(Extended {record.extended_count}x)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {record.duration_months} months
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {record.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {record.status === 'in_probation' || record.status === 'extended' ? (
                          <span className={`font-semibold ${
                            daysLeft < 0 ? 'text-red-600' : daysLeft <= 7 ? 'text-orange-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-slate-600'
                          }`}>
                            {daysLeft < 0 ? `${Math.abs(daysLeft)} overdue` : `${daysLeft} days`}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedRecord(record); setHistoryDialogOpen(true); }}
                            className="h-8 border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700/40"
                            data-testid={`view-history-btn-${record.probation_id}`}
                            title="View probation lifecycle history"
                          >
                            <History className="h-3 w-3 mr-1" />View History
                          </Button>
                          {canManage && (record.status === 'in_probation' || record.status === 'extended') && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => { setSelectedRecord(record); setActionType('confirm'); setActionRemarks(''); setActionDialogOpen(true); }}
                                className="bg-emerald-600 hover:bg-emerald-700 h-8 text-white"
                                data-testid={`confirm-probation-btn-${record.probation_id}`}
                              >
                                Confirm
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { setSelectedRecord(record); setActionType('extend'); setActionRemarks(''); setExtensionMonths(3); setActionDialogOpen(true); }}
                                className="h-8 border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:bg-amber-900/20 dark:hover:bg-amber-900/40"
                                data-testid={`extend-probation-btn-${record.probation_id}`}
                              >
                                Extend
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => { setSelectedRecord(record); setActionType('terminate'); setActionRemarks(''); setActionDialogOpen(true); }}
                                className="h-8"
                                data-testid={`terminate-probation-btn-${record.probation_id}`}
                              >
                                Terminate
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Probation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Create Probation Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label className="dark:text-slate-100">Employee *</Label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                required
              >
                <option value="">Select Employee</option>
                {availableEmployees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Only employees without existing probation records</p>
            </div>
            <div>
              <Label className="dark:text-slate-100">Probation Duration *</Label>
              <select
                value={formData.duration_months}
                onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) })}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                required
              >
                <option value={3}>3 Months</option>
                <option value={6}>6 Months</option>
                <option value={12}>12 Months</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              {actionType === 'confirm' && <CheckCircle className="h-5 w-5 text-emerald-500" />}
              {actionType === 'extend' && <RefreshCw className="h-5 w-5 text-blue-500" />}
              {actionType === 'terminate' && <XCircle className="h-5 w-5 text-red-500" />}
              {actionType === 'confirm' && 'Confirm Employee'}
              {actionType === 'extend' && 'Extend Probation'}
              {actionType === 'terminate' && 'Terminate Probation'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedRecord && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="font-medium text-slate-900 dark:text-slate-100">{selectedRecord.employee_name}</p>
                <p className="text-sm text-slate-500">
                  Probation ends: {new Date(selectedRecord.extended_to || selectedRecord.end_date).toLocaleDateString('en-MY')}
                </p>
              </div>
            )}
            
            {actionType === 'extend' && (
              <div>
                <Label className="dark:text-slate-100">Extension Duration *</Label>
                <select
                  value={extensionMonths}
                  onChange={(e) => setExtensionMonths(parseInt(e.target.value))}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value={1}>1 Month</option>
                  <option value={2}>2 Months</option>
                  <option value={3}>3 Months</option>
                  <option value={6}>6 Months</option>
                </select>
              </div>
            )}
            
            <div>
              <Label className="dark:text-slate-100">
                {actionType === 'confirm' ? 'Remarks (Optional)' : 'Remarks (Required)'}
              </Label>
              <Textarea
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                placeholder={
                  actionType === 'confirm' ? 'Add any remarks...' :
                  actionType === 'extend' ? 'Reason for extension...' :
                  'Reason for termination...'
                }
                className="dark:bg-slate-900"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleAction}
                className={
                  actionType === 'confirm' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  actionType === 'extend' ? 'bg-blue-600 hover:bg-blue-700' :
                  'bg-red-600 hover:bg-red-700'
                }
              >
                {actionType === 'confirm' && 'Confirm Employee'}
                {actionType === 'extend' && 'Extend Probation'}
                {actionType === 'terminate' && 'Terminate'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* View History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg dark:bg-slate-800" data-testid="probation-history-dialog">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <History className="h-5 w-5 text-emerald-500" />
              Probation History
            </DialogTitle>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              {/* Employee header */}
              <div className="flex items-start justify-between bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                <div>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">{selectedRecord.employee_name}</p>
                  <p className="text-xs text-slate-500 font-mono">{selectedRecord.employee_id}</p>
                </div>
                <Link
                  to={`/dashboard/employees/${selectedRecord.employee_id}`}
                  className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                  data-testid="history-open-employee-link"
                >
                  Open profile <ExternalLink className="h-3 w-3" />
                </Link>
              </div>

              {/* Timeline */}
              <div className="space-y-3">
                {/* 1. Created */}
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Calendar className="h-3 w-3 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 mt-1" />
                  </div>
                  <div className="flex-1 pb-3">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Probation Started</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(selectedRecord.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {' → '}
                      {new Date(selectedRecord.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Duration: <span className="font-medium">{selectedRecord.duration_months} months</span>
                    </p>
                  </div>
                </div>

                {/* 2. Extensions */}
                {selectedRecord.extended_count > 0 && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="h-6 w-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <RefreshCw className="h-3 w-3 text-amber-600 dark:text-amber-300" />
                      </div>
                      <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 mt-1" />
                    </div>
                    <div className="flex-1 pb-3">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        Extended {selectedRecord.extended_count}× → {new Date(selectedRecord.extended_to).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      {selectedRecord.extension_reason && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 italic">"{selectedRecord.extension_reason}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* 3. Final action */}
                {(selectedRecord.status === 'confirmed' || selectedRecord.status === 'terminated') && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center ${selectedRecord.status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                        {selectedRecord.status === 'confirmed'
                          ? <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-300" />
                          : <XCircle className="h-3 w-3 text-red-600 dark:text-red-300" />}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {selectedRecord.status === 'confirmed' ? 'Confirmed as permanent employee' : 'Probation terminated'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {selectedRecord.action_date
                          ? new Date(selectedRecord.action_date).toLocaleString('en-MY', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </p>
                      {selectedRecord.action_by && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">By: <span className="font-medium">{selectedRecord.action_by}</span></p>
                      )}
                      {selectedRecord.action_remarks && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 italic">"{selectedRecord.action_remarks}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Still in probation */}
                {(selectedRecord.status === 'in_probation' || selectedRecord.status === 'extended') && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                        <Clock className="h-3 w-3 text-slate-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                        Awaiting final decision —{' '}
                        {(() => {
                          const left = getDaysRemaining(selectedRecord.end_date, selectedRecord.extended_to);
                          return left < 0 ? `${Math.abs(left)} days overdue` : `${left} days left`;
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHistoryDialogOpen(false)}
              className="border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700"
              data-testid="close-history-dialog"
            >Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
